import { motion, useInView } from 'motion/react';
import { useRef } from 'react';
import { Database, Zap, Shield, BarChart3, Webhook, Bot, ArrowRight } from 'lucide-react';

export function SystemVisualization() {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, amount: 0.3 });

  const features = [
    {
      icon: Database,
      title: 'Coleta Inteligente',
      description: 'Rastreamento automático de todas interações',
      color: '#4ECDC4',
      position: { x: 0, y: 0 },
    },
    {
      icon: Shield,
      title: 'Bot Killer',
      description: 'Bloqueio em tempo real de tráfego falso',
      color: '#FF6B35',
      position: { x: 1, y: 0 },
    },
    {
      icon: Webhook,
      title: 'Webhooks',
      description: 'Integração instantânea com qualquer sistema',
      color: '#FFE66D',
      position: { x: 0, y: 1 },
    },
    {
      icon: BarChart3,
      title: 'Analytics 360°',
      description: 'Dashboards em tempo real com insights',
      color: '#10B981',
      position: { x: 1, y: 1 },
    },
  ];

  const dataFlow = [
    { from: 'Visitante', to: 'WPTT', label: 'Tracking' },
    { from: 'WPTT', to: 'Bot Killer', label: 'Validação' },
    { from: 'Bot Killer', to: 'Database', label: 'Armazena' },
    { from: 'Database', to: 'Analytics', label: 'Processa' },
    { from: 'Analytics', to: 'Você', label: 'Insights' },
  ];

  return (
    <section ref={ref} className="relative py-24 bg-gradient-to-b from-[#0B0E14] to-[#0A0E1A] overflow-hidden">
      {/* Animated Circuit Background */}
      <div className="absolute inset-0 opacity-10">
        <svg className="w-full h-full" xmlns="http://www.w3.org/2000/svg">
          <defs>
            <pattern id="circuit" x="0" y="0" width="100" height="100" patternUnits="userSpaceOnUse">
              <path d="M 10 50 L 50 50 L 50 10" stroke="#4ECDC4" strokeWidth="1" fill="none" />
              <circle cx="50" cy="50" r="3" fill="#4ECDC4" />
            </pattern>
          </defs>
          <rect width="100%" height="100%" fill="url(#circuit)" />
        </svg>
      </div>

      <div className="container mx-auto px-6 relative z-10">
        {/* Section Header */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.6 }}
          className="text-center mb-16"
        >
          <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full border border-[#4ECDC4]/30 bg-[#4ECDC4]/10 backdrop-blur-sm mb-6">
            <Zap className="w-4 h-4 text-[#4ECDC4]" />
            <span className="text-[#4ECDC4] font-mono text-sm uppercase tracking-wider">
              Como Funciona
            </span>
          </div>

          <h2 className="text-4xl md:text-5xl lg:text-6xl font-bold mb-6 text-white" style={{ fontFamily: 'Sora, sans-serif' }}>
            O Sistema Por Trás
            <br />
            <span className="bg-gradient-to-r from-[#4ECDC4] via-[#10B981] to-[#FFE66D] bg-clip-text text-transparent">
              Da Transformação
            </span>
          </h2>

          <p className="text-xl text-gray-400 max-w-3xl mx-auto">
            Tecnologia de ponta que trabalha <span className="text-[#4ECDC4]">24/7</span> para dar 
            <span className="text-[#10B981]"> controle total</span> dos seus dados
          </p>
        </motion.div>

        {/* Data Flow Diagram */}
        <div className="max-w-5xl mx-auto mb-16">
          <div className="flex flex-col md:flex-row items-center justify-between gap-4 mb-12">
            {dataFlow.map((flow, index) => (
              <div key={index} className="flex items-center gap-4">
                <motion.div
                  initial={{ opacity: 0, scale: 0.8 }}
                  animate={isInView ? { opacity: 1, scale: 1 } : {}}
                  transition={{ duration: 0.4, delay: index * 0.15 }}
                  className="relative"
                >
                  <div className="px-6 py-3 rounded-xl border border-[#4ECDC4]/30 bg-gradient-to-br from-[#4ECDC4]/20 to-[#0B0E14] backdrop-blur-sm">
                    <span className="text-white font-semibold">{flow.from}</span>
                  </div>
                  
                  {/* Pulse effect */}
                  <motion.div
                    className="absolute inset-0 rounded-xl bg-[#4ECDC4]/30 blur-xl"
                    animate={{
                      scale: [1, 1.2, 1],
                      opacity: [0.3, 0.6, 0.3],
                    }}
                    transition={{
                      duration: 2,
                      repeat: Infinity,
                      delay: index * 0.3,
                    }}
                  />
                </motion.div>

                {index < dataFlow.length - 1 && (
                  <motion.div
                    initial={{ opacity: 0, x: -20 }}
                    animate={isInView ? { opacity: 1, x: 0 } : {}}
                    transition={{ duration: 0.4, delay: index * 0.15 + 0.2 }}
                    className="hidden md:flex flex-col items-center gap-1"
                  >
                    <ArrowRight className="w-6 h-6 text-[#4ECDC4]" />
                    <span className="text-xs text-gray-500 font-mono">{flow.label}</span>
                  </motion.div>
                )}
              </div>
            ))}
          </div>
        </div>

        {/* Features Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-5xl mx-auto mb-16">
          {features.map((feature, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, y: 30 }}
              animate={isInView ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.6, delay: 0.4 + index * 0.1 }}
              className="relative group"
            >
              <div className="p-8 rounded-2xl border border-white/10 bg-gradient-to-br from-white/5 to-transparent backdrop-blur-lg hover:border-white/20 transition-all duration-300">
                {/* Icon */}
                <div 
                  className="w-16 h-16 rounded-2xl flex items-center justify-center mb-6 relative"
                  style={{ 
                    background: `linear-gradient(135deg, ${feature.color}20 0%, ${feature.color}05 100%)`,
                    border: `1px solid ${feature.color}30`,
                  }}
                >
                  <feature.icon className="w-8 h-8" style={{ color: feature.color }} />
                  
                  {/* Animated ring */}
                  <motion.div
                    className="absolute inset-0 rounded-2xl"
                    style={{ border: `2px solid ${feature.color}` }}
                    animate={{
                      scale: [1, 1.2, 1],
                      opacity: [0.5, 0, 0.5],
                    }}
                    transition={{
                      duration: 2,
                      repeat: Infinity,
                      delay: index * 0.5,
                    }}
                  />
                </div>

                {/* Content */}
                <h3 className="text-2xl font-bold mb-3 text-white" style={{ fontFamily: 'Sora, sans-serif' }}>
                  {feature.title}
                </h3>
                <p className="text-gray-400 text-lg">
                  {feature.description}
                </p>

                {/* Connecting lines (visual effect) */}
                <motion.div
                  className="absolute inset-0 rounded-2xl opacity-0 group-hover:opacity-100 transition-opacity duration-300"
                  style={{
                    background: `radial-gradient(circle at 50% 50%, ${feature.color}10 0%, transparent 70%)`,
                  }}
                />
              </div>
            </motion.div>
          ))}
        </div>

        {/* Central Hub Visualization */}
        <motion.div
          initial={{ opacity: 0, scale: 0.9 }}
          animate={isInView ? { opacity: 1, scale: 1 } : {}}
          transition={{ duration: 0.8, delay: 0.8 }}
          className="max-w-4xl mx-auto"
        >
          <div className="relative p-12 rounded-3xl border-2 border-[#4ECDC4]/30 bg-gradient-to-br from-[#4ECDC4]/10 via-[#0B0E14] to-[#10B981]/10 overflow-hidden">
            {/* Animated Pulse Background */}
            <motion.div
              className="absolute inset-0"
              animate={{
                background: [
                  'radial-gradient(circle at 50% 50%, rgba(78, 205, 196, 0.2) 0%, transparent 50%)',
                  'radial-gradient(circle at 50% 50%, rgba(16, 185, 129, 0.2) 0%, transparent 50%)',
                  'radial-gradient(circle at 50% 50%, rgba(78, 205, 196, 0.2) 0%, transparent 50%)',
                ],
              }}
              transition={{ duration: 4, repeat: Infinity }}
            />

            <div className="relative z-10 text-center">
              {/* Central Logo/Icon */}
              <motion.div
                className="w-24 h-24 mx-auto mb-8 rounded-3xl bg-gradient-to-br from-[#4ECDC4] to-[#10B981] flex items-center justify-center relative"
                animate={{
                  boxShadow: [
                    '0 0 20px rgba(78, 205, 196, 0.5)',
                    '0 0 40px rgba(16, 185, 129, 0.8)',
                    '0 0 20px rgba(78, 205, 196, 0.5)',
                  ],
                }}
                transition={{ duration: 2, repeat: Infinity }}
              >
                <Zap className="w-12 h-12 text-white" />
                
                {/* Orbiting particles */}
                {[0, 1, 2, 3].map((i) => (
                  <motion.div
                    key={i}
                    className="absolute w-3 h-3 rounded-full bg-[#FFE66D]"
                    style={{
                      left: '50%',
                      top: '50%',
                    }}
                    animate={{
                      x: [
                        Math.cos((i * Math.PI) / 2) * 60,
                        Math.cos((i * Math.PI) / 2 + Math.PI / 4) * 60,
                        Math.cos((i * Math.PI) / 2 + Math.PI / 2) * 60,
                        Math.cos((i * Math.PI) / 2 + (3 * Math.PI) / 4) * 60,
                        Math.cos((i * Math.PI) / 2) * 60,
                      ],
                      y: [
                        Math.sin((i * Math.PI) / 2) * 60,
                        Math.sin((i * Math.PI) / 2 + Math.PI / 4) * 60,
                        Math.sin((i * Math.PI) / 2 + Math.PI / 2) * 60,
                        Math.sin((i * Math.PI) / 2 + (3 * Math.PI) / 4) * 60,
                        Math.sin((i * Math.PI) / 2) * 60,
                      ],
                    }}
                    transition={{
                      duration: 4,
                      repeat: Infinity,
                      ease: 'linear',
                    }}
                  />
                ))}
              </motion.div>

              <h3 className="text-3xl md:text-4xl font-bold mb-4 text-white" style={{ fontFamily: 'Sora, sans-serif' }}>
                Tudo em <span className="text-[#4ECDC4]">Um Único Sistema</span>
              </h3>
              <p className="text-xl text-gray-300 mb-8 max-w-2xl mx-auto">
                Enquanto você foca no crescimento, o WPTT cuida de toda a complexidade técnica
              </p>

              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <motion.button
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  className="px-8 py-4 bg-gradient-to-r from-[#4ECDC4] to-[#10B981] rounded-xl font-semibold text-white shadow-2xl"
                >
                  Ver Demo Interativa
                </motion.button>
                <motion.button
                  whileHover={{ scale: 1.05 }}
                  className="px-8 py-4 border-2 border-[#4ECDC4]/50 rounded-xl font-semibold text-[#4ECDC4] hover:bg-[#4ECDC4]/10 transition-all"
                >
                  Documentação Técnica
                </motion.button>
              </div>
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  );
}
